import { useEffect, useState } from "react";
import { http, fmtINR, fmtNum, fmtPct } from "@/lib/api";
import { Sparkles } from "lucide-react";
import CopilotPanel from "@/components/CopilotPanel";

export default function CopilotPage() {
    const [ctx, setCtx] = useState(null);
    const [open, setOpen] = useState(true);

    useEffect(() => {
        http.get("/copilot/context").then((r) => setCtx(r.data));
    }, []);

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">AI Copilot</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Governed Business Intelligence</h1>
                </div>
                <button
                    onClick={() => setOpen(true)}
                    data-testid="open-copilot-page-btn"
                    className="flex items-center gap-1.5 rounded-sm bg-[hsl(var(--ai))] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[hsl(var(--ai))]/90"
                >
                    <Sparkles className="h-3.5 w-3.5" /> Open Copilot
                </button>
            </div>

            {ctx && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <ContextCard label="Sell-Out · 30d" value={fmtINR(ctx.sell_out.value_last_30d)} />
                    <ContextCard label="Sell-Out · 7d" value={fmtINR(ctx.sell_out.value_last_7d)} />
                    <ContextCard
                        label={`Target · ${ctx.period}`}
                        value={fmtPct(ctx.target_this_month.achievement_pct)}
                        hint={`${fmtINR(ctx.target_this_month.achieved)} / ${fmtINR(ctx.target_this_month.target)}`}
                    />
                    <ContextCard
                        label="Network"
                        value={`${fmtNum(ctx.network.stores)} stores`}
                        hint={`${fmtNum(ctx.network.distributors)} distributors · ${fmtNum(ctx.network.skus)} SKUs`}
                    />
                </div>
            )}

            <div className="rounded-sm border border-border bg-card p-4">
                <div className="mlabel mb-3">How Copilot answers</div>
                <ol className="space-y-2 text-[12px] leading-relaxed text-muted-foreground">
                    <li>
                        <span className="mono-num mr-2 text-foreground">01</span>
                        A governed semantic bundle is computed for your tenant — KPIs, top exceptions, active schemes,
                        open alerts, health scores.
                    </li>
                    <li>
                        <span className="mono-num mr-2 text-foreground">02</span>
                        Claude Sonnet 4.5 reasons over that bundle only. No unrestricted DB access.
                    </li>
                    <li>
                        <span className="mono-num mr-2 text-foreground">03</span>
                        Every number cites the bundle field. Every recommendation includes confidence & reasoning.
                    </li>
                    <li>
                        <span className="mono-num mr-2 text-foreground">04</span>
                        You can convert a recommendation into an approved action via the alerts & workflow engine.
                    </li>
                </ol>
            </div>

            <CopilotPanel open={open} onClose={() => setOpen(false)} />
        </div>
    );
}

function ContextCard({ label, value, hint }) {
    return (
        <div className="rounded-sm border border-border bg-card p-4">
            <div className="mlabel">{label}</div>
            <div className="mono-num mt-1 text-2xl font-semibold">{value}</div>
            {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
        </div>
    );
}
